import { type Enum } from '@webapp/infrastructure/types/Enum'

/**
 * Identifiers for some of the interactive fields of an OrderItem / OrderDraftItem. These fields
 * are applicable to the OrderItemCard and OrderItemForm components.
 * Note that this is not an exhaustive list of all the fields that can be interacted with. These
 * are identifiers for the fields/values which can be interacted with and can be in an
 * invalid state. This data is centralized in this enum and connects business logic to UI elements.
 *
 * For custom-field identifiers, use `getCustomFieldId(id)` as these IDs are
 * computed with the custom field's ID.
 */
export const OrderItemInteractiveFields = {
  // Common
  NAME: 'name',
  SKU: 'sku',
  CURRENCY: 'currency',
  UNIT: 'unitType',
  VENDOR: 'preferredVendor',
  VENDOR_NAME: 'vendorName',
  ACCOUNT_CODE: 'account',
  ATTACHMENTS: 'attachments',
  COMMENT: 'comment',

  // One-Time
  PRICE: 'price',
  QUANTITY: 'quantity',

  // Recurring
  RECURRING_START_DATE: 'recurrenceStartDate',
  RECURRING_FREQUENCY: 'recurrenceFrequency',
  RECURRING_DURATION: 'recurrenceDuration',
  RECURRING_DURATION_QUANTITY: 'recurrenceDurationQuantity',
  RECURRING_PRICE: 'recurringPrice',

  // Blanket
  BLANKET_AMOUNT: 'blanketAmount',
  BLANKET_QUANTITY: 'blanketQuantity',
  BLANKET_PRICE: 'blanketPrice',

  // IDs specific to the OrderItemCard (item fields are grouped into single clickable elements)
  RECURRING_ITEM_DETAILS: 'recurringItemDetails',
  BLANKET_ITEM_AMOUNT: 'blanketItemAmount',
  BLANKET_ITEM_UNIT_COST: 'blanketItemUnitCost',
  BLANKET_ITEM_QUANTITY: 'blanketItemQuantity',
} as const

export type OrderItemInteractiveFieldEnum = Enum<
  typeof OrderItemInteractiveFields
>

export const ORDER_ITEM_FORM_TOP_ID = 'order-item-form-top'

const CUSTOM_FIELD_ID_PREFIX = 'custom-field-'

/**
 * Computes a unique identifier for a custom field.
 */
export const getCustomFieldId = (customFieldId: number) =>
  `${CUSTOM_FIELD_ID_PREFIX}${customFieldId}` as const

export type OrderItemCustomFieldId = ReturnType<typeof getCustomFieldId>

/**
 * Narrows a field id to a custom-field id (as produced by `getCustomFieldId`).
 */
export const isCustomFieldId = (id: string): id is OrderItemCustomFieldId =>
  id.startsWith(CUSTOM_FIELD_ID_PREFIX)
